import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../../shared/API/apiClient";
import { getPrincipleDashboard } from "../../../services/principleService";
import { useReportingYear } from "../../../context/ReportingYearContext";
import { LayoutGrid, CheckCircle, Clock, Cpu, Bold } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Sparkles } from "lucide-react";


export default function PrincipleDashboard() {
  const [principles, setPrinciples] = useState([]);
  const [loading, setLoading] = useState(true);

  const [legalEntities, setLegalEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState("");

  const { reportingYear, reportingYearId } = useReportingYear(); // reporting year

  const [summary, setSummary] = useState({
    total_principles: 0,
    completed: 0,
    pending: 0,
    auto: 0,
  });
const FA_ICON_MAP = {
  "fa-draft":           "fa-solid fa-pen-to-square",
  "fa-submitted":       "fa-solid fa-paper-plane",
  "fa-reviewed":        "fa-solid fa-circle-check",
  "fa-approved":        "fa-solid fa-check-double",
  "fa-locked":          "fa-solid fa-lock",
  "fa-notstarted":      "fa-solid fa-clock",
  "fa-rework":          "fa-solid fa-rotate-left",
  "fa-list":            "fa-solid fa-list",
  "fa-exclamation":     "fa-solid fa-circle-exclamation",
  "fa-paperclip":       "fa-solid fa-paperclip",
};

  const navigate = useNavigate();

  const location = useLocation();

  useEffect(() => {
    if (location.state?.entityId) {
      setSelectedEntity(location.state.entityId);
    }
  }, []);

  //legal entities dropdown API

  useEffect(() => {
    const fetchLegalEntities = async () => {
      if (!reportingYearId) return;

      try {
        const res = await apiClient.get(`/api/legal-entities-by-year`, {
          params: {
            reporting_year_id: reportingYearId,
          },
        });

        if (res.data.code === 200) {
          const entities = res.data.data || [];

        setLegalEntities(entities);

        const entityId = location.state?.legalEntityId;

        if (entityId) {

            const exists = entities.some(
                e => Number(e.entity_id) === Number(entityId)
            );

            if (exists) {
                setSelectedEntity(String(entityId));
            }

        }
        } else {
          setLegalEntities([]);
        }
      } catch (err) {
        console.error("Legal entities API error:", err);
        setLegalEntities([]);
      }
    };

    fetchLegalEntities();
  }, [reportingYearId]); // dependency

  //Reset selected entity when year changes
  useEffect(() => {
    if (!location.state?.entityId) {
      setSelectedEntity("");
    }
  }, [reportingYearId]);

  //finding entity name for display to next page

  const selectedEntityName = legalEntities.find(
    (e) => e.entity_id == selectedEntity
  )?.entity_name;

  //API call with reporting year
  const fetchPrinciples = useCallback(async () => {
    if (!reportingYearId || !selectedEntity) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await getPrincipleDashboard(
        selectedEntity,
        reportingYearId
      );

      if (response.data.code === 200) {
        setPrinciples(response.data.principles || []);
        setSummary(response.data.summary || {});
      } else {
        setPrinciples([]);
      }
    } catch (error) {
      console.error("API Error:", error);
      setPrinciples([]);
    } finally {
      setLoading(false);
    }
  }, [reportingYearId, selectedEntity]);

  useEffect(() => {
    fetchPrinciples();
  }, [fetchPrinciples]);

  if (loading && selectedEntity) {
    return (
      <div className="dash-loader-wrapper">
        <div className="dash-loader"></div>
        <p className="dash-loader-text">Principle Progress...</p>
      </div>
    );
  }

  return (
    <div className="dash-page">
      <div className="dash-page-title">BRSR Disclosures Progress</div>
      <div className="dash-page-subtitle">
        Track completion across all 9 BRSR Principles (Section B & C)
      </div>

      {/* 🔷 FILTER CARD */}
      <div className="filter-card">
        <div className="filter-group">
          <label>Reporting Year *</label>
          <input
            className="filter-input"
            value={reportingYear || ""}
            disabled
          ></input>
          
        </div>

        <div className="filter-group">
          <label>Legal Entity *</label>
          <select
            className="filter-input"
            value={selectedEntity}
            onChange={(e) => {
              setSelectedEntity(e.target.value);
              setLoading(true);
            }}
          >
            <option value="">Select</option>
            {legalEntities.map((entity) => (
              <option key={entity.entity_id} value={entity.entity_id}>
                {entity.entity_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 🔷 STAT CARDS (STATIC FOR NOW) */}
      {selectedEntity && (
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-icon gray">
              <LayoutGrid size={20} />
            </div>
            <div>
              <div className="stat-title">Total Principles</div>
              <div className="stat-value">{summary.total_principles}</div>
            </div>
          </div>

          <div className="stat-card green">
            <div className="stat-icon green">
              <CheckCircle size={20} />
            </div>
            <div>
              <div className="stat-title">Completed</div>
              <div className="stat-value">{summary.completed}</div>
            </div>
          </div>

          <div className="stat-card yellow">
            <div className="stat-icon yellow">
              <Clock size={20} />
            </div>
            <div>
              <div className="stat-title">Pending</div>
              <div className="stat-value">{summary.pending}</div>
            </div>
          </div>

          <div className="stat-card blue">
            <div className="stat-icon blue">
              <Cpu size={20} />
            </div>
            <div>
              <div className="stat-title">Auto (System)</div>
              <div className="stat-value">{summary.auto_calculated}</div>
            </div>
          </div>
        </div>
      )}
{!selectedEntity && (
        <div className="st-empty-entity">
          <div className="st-empty-entity__icon">
            <i className="fa-solid fa-building" />
          </div>
          <div className="st-empty-entity__title">Select a Legal Entity to continue</div>
          <div className="st-empty-entity__sub">
            Select a legal entity above to track disclosure progress across all BRSR Principles and take required actions.
          </div>
        </div>
      )}
      {selectedEntity && (
        <div className="table-card">
          <div className="table-header">
            <div>PRINCIPLE</div>
            <div>DESCRIPTION</div>
            <div>STATUS</div>
            <div>% COMPLETE</div>
            <div>ACTION</div>
          </div>

          {principles.map((item) => (
            <div className="table-row" key={item.principle_id}>
              <div className="pcode">{item.principle_code}</div>

              <div>
                <div className="question-text">{item.title}</div>
                <div className="p-desc">{item.description}</div>
              </div>

              <div className="status-wrapper">
              <span
                            className="status"
                            style={{
                              "--badge-bg": item.status_color,
                              "--badge-fg": item.status_fore_color,
                            }}
                          >
                            {item.status_icon && (
                              <i className={`fa-solid ${item.status_icon ?? "fa-solid fa-circle"} status_icon_size`} />
                            )}
                            {item.status_label}
                          </span>

                {item.is_auto_calculated && (
                  <span className="status auto-badge">
                    <Sparkles size={10} /> Auto-calculated
                  </span>
                )}
              </div>

              <div className="progress-box">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${item.completion_percentage}%`,
                      backgroundColor: item.progress_color,
                    }}
                  ></div>
                </div>

                <div className="progress-meta">
                  <span className="progress-percent">
                    {item.completion_percentage}%
                  </span>

                  <span className="progress-count">
                    ({item.answered_questions}/{item.total_questions})
                  </span>
                </div>
              </div>

              <div className="action"> 
                {item.action ? (
                  <span
                    className="action-link"
                    onClick={() =>
                      navigate(item.action.url, {
                        state: {
                          entityName: selectedEntityName,
                          entityId: selectedEntity,
                          yearLabel: reportingYear,
                          sourcePage:"principle-dashboard"
                        },
                      })
                    }
                  >
                    {item.action.label} →
                  </span>
                ) : (
                  <span className="auto-label">Auto</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
